import * as CL from '@design-system/component-library';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRevalidator, useRouteLoaderData } from 'react-router';
import { dsClass } from '../../../common/constants/dsClasses.js';
import { useSyncCartPricesWithModels } from '../../../common/hooks/useSyncCartPricesWithModels.js';
import { monthMsg, nextMsg, productInformationMsg, recurringChargesMsg, t } from '../../../common/i18n/index.js';
import { scrollTo } from '../../../common/utils/browserUtils.js';
import { getConfiguredCommercialProducts, physicalProducts } from '../../../common/utils/cartProductUtils.js';
import { getTotalSums } from '../../../common/utils/commercialProductUtils.js';
import { getIndexFromParent, getTopMostElement } from '../../../common/utils/domUtils.js';
import { getElementsWithErrors } from '../../../common/utils/errorUtils.js';
import { deepEqual } from '../../../common/utils/objectUtils.js';
import { formatSum } from '../../../common/utils/priceUtils.js';
import { useAuth } from '../../../public/site/AuthProvider.js';
import { useSessionData } from '../../../public/site/SessionDataProvider.js';
import { getUnattemptedOnlineModelCodes } from '../../../selfservice/common/shopping-cart/shoppingCartFunctions.js';
import { useContactsEdit } from '../../ContactsEditContext/ContactsEditContext.js';
import { DeviceCheckoutCartProduct } from '../../DeviceCheckoutCartProduct/DeviceCheckoutCartProduct.js';
import {
  evaluateAdditionalParametersFns,
  evaluateDomRefFns,
  evaluateGetContactOrPurposeOfUseFns,
  getCartItemUniqueKey,
  saveAdditionalParametersInCartItems,
} from '../../DeviceCheckoutCartProduct/deviceCheckoutCartProductUtil.js';
import { OrderSummary, OrderSummaryType } from '../../OrderSummary/OrderSummary.js';
import { SquareTradeErrorModal, hasCartForbiddenSquareTradeAddOn } from '../../SquareTradeErrorModal/index.js';
import { FormWrapper } from '../FormWrapper.js';
import type { CheckoutRootLoaderData } from '../../../common/loaders.js';
import type { ShoppingCartItemForCheckout } from '../../../common/types/checkout.js';
import type { DeviceChangeRequest } from '../../../common/types/device.js';
import type { SelectedPhoneNumber } from '../../../common/types/subscription.js';
import type { OnlineModelCategory } from '../../../generated/api/models.js';
import type { AuthenticatedUser } from '../../../public/site/authState';
import type { ActionsHistory, State } from '../../../selfservice/common/store.js';
import type { ContactOrPurposeOfUseCallables } from '../../ContactOrPurposeOfUse/ContactOrPurposeOfUse.js';
import type { DeviceChangeSelectedContactIndex } from '../../DeviceCheckoutCartProduct/DeviceCheckoutCartProduct.js';
import type { DeviceCheckoutDeliveryDetailsType } from '../../DeviceCheckoutDeliveryDetails/DeviceCheckoutDeliveryDetailsUtils.js';
import type { OrderConfiguration } from '../../OrderSubscriptionConfiguration/FormWrapper.js';
import type { CheckoutStepItem } from '../EoeCheckoutSteps.js';
import type { MouseEvent } from 'react';

export interface CartProductsStepProps {
  shouldScrollToError: boolean;
  setShouldScrollToError: (value: boolean) => void;
  setDeviceChangeRequest: (value?: DeviceChangeRequest) => void;
  deviceChangeRequest?: DeviceChangeRequest;
  deliveryDetails?: DeviceCheckoutDeliveryDetailsType;
  user?: AuthenticatedUser & ActionsHistory;
}

export type AliasSupplier = () => {
  obj: string | boolean | SelectedPhoneNumber;
};

export const CartProductsStepContent = ({
  shouldScrollToError,
  setShouldScrollToError,
  setDeviceChangeRequest,
  deliveryDetails,
  user,
}: CartProductsStepProps) => {
  const dispatch = useDispatch();
  const cartProductDetailsFns = useRef<Record<string, Record<string, AliasSupplier>>>({});
  const [deviceChangeSelectedContactIndex, setDeviceChangeSelectedContactIndex] =
    useState<DeviceChangeSelectedContactIndex>();
  const [showSquareTradeErrorModal, setShowSquareTradeErrorModal] = useState(false);

  const domRefFns = useRef<Record<string, () => { [parentKey: string]: { [elementKey: string]: HTMLElement } }>>({});

  let getContactOrPurposeOfUseFns: Record<string, Record<number, ContactOrPurposeOfUseCallables['getValues']>> = {};

  const { ssoSessionValid } = useAuth();
  const { saving: contactsSaving } = useContactsEdit();
  const { revalidate } = useRevalidator();
  const attemptedOnlineModelCodes = useRef(new Set<string>());

  const { cartItems, duplicateCheckInProgress, disableNextButton } = useSelector(
    (state: State) => ({
      cartItems: state.deviceCheckout?.cartItems || [],
      duplicateCheckInProgress: state.deviceCheckout?.duplicateContactCheck?.inProgress,
      disableNextButton: contactsSaving,
    }),
    deepEqual
  );
  const companyInfo = useSessionData().companyInfo;
  const enrollmentPrograms = companyInfo?.enrollmentPrograms;

  const { models: onlineModels } = useRouteLoaderData('checkoutRootEmployee') as CheckoutRootLoaderData;
  const models = useMemo(() => onlineModels || [], [onlineModels]);
  useSyncCartPricesWithModels(models);

  // checkoutRootLoader already fetches models for every cart item present at load time; if the cart
  // changes afterwards, revalidate the loader instead of dispatching a per-item Redux load.
  useEffect(() => {
    const physicalCartModelCodes = cartItems
      .filter(cartItem => physicalProducts.includes(cartItem.category.toUpperCase() as OnlineModelCategory))
      .map(cartItem => cartItem.onlineModelCode);
    const modelCodesToLoad = getUnattemptedOnlineModelCodes(
      physicalCartModelCodes,
      models,
      attemptedOnlineModelCodes.current
    );
    modelCodesToLoad.forEach(code => attemptedOnlineModelCodes.current.add(code));
    if (modelCodesToLoad.length > 0) {
      revalidate();
    }
  }, [cartItems, models, revalidate, ssoSessionValid]);

  useEffect(() => {
    if (cartItems.length > 0 && shouldScrollToError) {
      const topMostCartItemWithErrors = cartItems.findIndex(
        cartItem =>
          cartItem.errors !== undefined &&
          cartItem.errors
            .map(error => error.parent)
            .filter(
              parent => parent !== undefined && cartItem.errors?.find(error => error.parent === parent) !== undefined
            )
            .sort((parent, otherParent) => getIndexFromParent(parent!) - getIndexFromParent(otherParent!))[0] !==
            undefined
      );
      if (topMostCartItemWithErrors !== -1) {
        const validationErrors = cartItems[topMostCartItemWithErrors].errors;
        const cartItemUniqueKey = getCartItemUniqueKey(cartItems[topMostCartItemWithErrors]);

        if (validationErrors) {
          const topMostParentWithErrors = validationErrors
            .map(error => error.parent)
            .filter(
              parent => parent !== undefined && validationErrors.find(error => error.parent === parent) !== undefined
            )
            .sort((parent, otherParent) => getIndexFromParent(parent!) - getIndexFromParent(otherParent!))[0];

          if (topMostParentWithErrors) {
            scrollTo(
              getTopMostElement(
                getElementsWithErrors(
                  domRefFns.current![`cartItemDomRef[${cartItemUniqueKey}]`]()[topMostParentWithErrors],
                  validationErrors
                )
              )
            );
            setShouldScrollToError(false);
          }
        }
      }
    }
  }, [shouldScrollToError, cartItems, domRefFns.current]); /* TODO: rules-of-hooks */ // eslint-disable-line react-hooks/exhaustive-deps

  const handleGetCartItemDomRefFns = (
    cartItemDomRefs: () => { [parentKey: string]: { [elementKey: string]: HTMLElement } },
    cartItemUniqueKey: string,
    cartProductUnMounted?: boolean
  ) => {
    domRefFns.current = evaluateDomRefFns(domRefFns.current, cartItemDomRefs, cartItemUniqueKey, cartProductUnMounted);
  };

  const handleGetAdditionalParametersFns = (
    cartItemUniqueKey: string,
    collectAdditionalParametersFn: Record<string, AliasSupplier>,
    cartProductUnMounted?: boolean
  ) => {
    cartProductDetailsFns.current = evaluateAdditionalParametersFns(
      cartProductDetailsFns.current,
      collectAdditionalParametersFn,
      cartItemUniqueKey,
      cartProductUnMounted
    );
  };

  const handleSetGetContactOrPurposeOfUseFn = (
    cartItemUniqueKey: string,
    productItemIndex?: number,
    fn?: ContactOrPurposeOfUseCallables['getValues']
  ) => {
    const value = evaluateGetContactOrPurposeOfUseFns(
      getContactOrPurposeOfUseFns,
      cartItemUniqueKey,
      productItemIndex,
      fn
    );
    if (value) {
      getContactOrPurposeOfUseFns = value;
    }
  };

  const handleSubmit = async (validateOnly: boolean, data?: OrderConfiguration) => {
    // Do no scroll to error if form has errors as in that case scroll to error is handled within form
    setShouldScrollToError(
      !(await saveAdditionalParametersInCartItems(
        validateOnly,
        cartProductDetailsFns.current,
        getContactOrPurposeOfUseFns,
        cartItems,
        dispatch,
        deliveryDetails,
        enrollmentPrograms,
        onlineModels,
        undefined,
        data
      ))
    );
    if (!validateOnly) {
      revalidate();
    }
  };

  const handleNextClicked = (event: MouseEvent<HTMLButtonElement>) => {
    if (hasCartForbiddenSquareTradeAddOn(cartItems, user)) {
      event.preventDefault();
      setShowSquareTradeErrorModal(true);
      return false;
    }

    return true;
  };

  const configuredCommercialProducts = getConfiguredCommercialProducts(cartItems);

  return (
    <FormWrapper onSuccess={data => handleSubmit(false, data)} onError={() => handleSubmit(true)} cartItems={cartItems}>
      <div>
        <SquareTradeErrorModal isOpen={showSquareTradeErrorModal} onClose={() => setShowSquareTradeErrorModal(false)} />
        <div className="of-checkout__product-list">
          {cartItems.map(cartItem => {
            const cartItemUniqueKey = getCartItemUniqueKey(cartItem);
            return (
              <div key={cartItemUniqueKey} className="of-checkout__product-list--bordered">
                <DeviceCheckoutCartProduct
                  user={user}
                  cartItem={cartItem}
                  getCartItemDomRefFns={(
                    cartItemDomRefs: () => { [parentKey: string]: { [elementKey: string]: HTMLElement } },
                    cartProductUnMounted?: boolean
                  ) => handleGetCartItemDomRefFns(cartItemDomRefs, cartItemUniqueKey, cartProductUnMounted)}
                  getAdditionalParametersFns={(
                    collectAdditionalParametersFn: Record<string, AliasSupplier>,
                    cartProductUnMounted?: boolean
                  ) =>
                    handleGetAdditionalParametersFns(
                      cartItemUniqueKey,
                      collectAdditionalParametersFn,
                      cartProductUnMounted
                    )
                  }
                  setGetContactOrPurposeOfUseFn={(productItemIndex, fn) =>
                    handleSetGetContactOrPurposeOfUseFn(cartItemUniqueKey, productItemIndex, fn)
                  }
                  deviceChangeSelectedContactIndex={deviceChangeSelectedContactIndex}
                  onUpdateDeviceChangeSelection={(
                    value?: DeviceChangeRequest,
                    selectedIndex?: DeviceChangeSelectedContactIndex
                  ) => {
                    setDeviceChangeRequest(value);
                    setDeviceChangeSelectedContactIndex(selectedIndex);
                  }}
                  shoppingCartMode
                />
              </div>
            );
          })}
        </div>
        <OrderSummary
          priceIncludesVAT={true}
          summaryType={OrderSummaryType.DETAILS_CLOSED}
          commercialProducts={configuredCommercialProducts}
        />
        <div className={dsClass.PADDING_TOP_4}>
          <CL.Button
            className="of-next-button"
            disabled={disableNextButton && !duplicateCheckInProgress}
            loading={Boolean(duplicateCheckInProgress)}
            onClick={handleNextClicked}
            size="l"
            type="submit"
          >
            {t.F0MY(nextMsg).toUpperCase()}
          </CL.Button>
        </div>
      </div>
    </FormWrapper>
  );
};

export const cartProductsStep = (
  props: CartProductsStepProps,
  cartItems: Array<ShoppingCartItemForCheckout>
): CheckoutStepItem => {
  const totalSums = (totalCartItems: Array<ShoppingCartItemForCheckout>) =>
    getTotalSums(getConfiguredCommercialProducts(totalCartItems));

  const cartItemsTotal = cartItems.reduce((total, item) => total + item.quantity, 0);

  const isYttPeriodicOrder = cartItems.some(item => item.price.periodic?.billingPeriod);
  const completedName = isYttPeriodicOrder
    ? `${cartItemsTotal} ${t.UJRP('products')}: ${t.J4WW(recurringChargesMsg)} ${formatSum(
        totalSums(cartItems).totalMonthlyRecurringCharge
      )}, ${t.OD0K('one-time payments')} ${formatSum(totalSums(cartItems).totalOneTimeCharge)}`
    : `${cartItemsTotal} ${t.UJRP('products')}: ${formatSum(
        totalSums(cartItems).totalMonthlyRecurringCharge
      )}/${t.XXVX(monthMsg)}, ${t.OD0K('one-time payments')} ${formatSum(totalSums(cartItems).totalOneTimeCharge)}`;

  return {
    id: 'cartProductStep',
    content: <CartProductsStepContent {...props} />,
    name: t.R4EV(productInformationMsg),
    completedName: completedName,
  };
};
